import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft, Stack } from "@phosphor-icons/react";

export default function PackingListView() {
  const { id } = useParams();
  const [pl, setPl] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [settings, setSettings] = useState({ company_name: "US Operations" });

  useEffect(() => {
    (async () => {
      const [p, allBoxes, s] = await Promise.all([
        api.get(`/packing-lists/${id}`),
        api.get("/boxes"),
        api.get("/settings"),
      ]);
      setPl(p.data);
      setBoxes(allBoxes.data.filter((b) => p.data.box_ids.includes(b.id)));
      setSettings(s.data);
    })();
  }, [id]);

  if (!pl) return <div className="font-display uppercase">Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="pl-view-page">
      <div className="flex items-center justify-between no-print">
        <Link to="/packing-lists" className="brut-tag brut-tag-white inline-flex items-center gap-1">
          <ArrowLeft size={10} weight="bold" /> Back
        </Link>
        <div className="flex gap-2">
          {boxes.length > 0 && (
            <Link
              to={`/boxes/labels?ids=${boxes.map((b) => b.id).join(",")}`}
              className="brut-btn px-4 py-2 inline-flex items-center gap-2"
              data-testid="print-all-box-labels-btn"
              title={`Print 4×6 labels for all ${boxes.length} boxes in this packing list`}
            >
              <Stack size={14} weight="bold" /> Print {boxes.length} Box Label{boxes.length === 1 ? "" : "s"}
            </Link>
          )}
          <button onClick={() => window.print()} data-testid="print-pl-btn" className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2">
            <Printer size={14} weight="bold" /> Print Packing List
          </button>
        </div>
      </div>

      <div className="brut-card p-8 print-section" data-testid="pl-document">
        <div className="flex justify-between items-start border-b-2 border-[#334155] pb-4">
          <div className="flex items-start gap-3">
            <img src="/logo-gate.jpg" alt="GATE" className="w-16 h-16 brut-border object-cover shrink-0" />
            <div>
              <div className="brut-tag brut-tag-yellow mb-1">Packing List</div>
              <div className="font-display text-3xl uppercase tracking-tighter">{settings.company_name}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="brut-label">PL Number</div>
            <div className="font-display text-2xl">{pl.pl_number}</div>
            <div className="brut-label mt-2">Ship Date</div>
            <div className="font-mono-ui">{pl.ship_date}</div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mt-5">
          <div className="brut-border p-3 col-span-2">
            <div className="brut-label">Customer</div>
            <div className="font-display text-xl uppercase">{pl.customer}</div>
          </div>
          <div className="brut-border p-3">
            <div className="brut-label">Total Boxes</div>
            <div className="font-display text-3xl">{pl.total_boxes}</div>
          </div>
          <div className="brut-border p-3">
            <div className="brut-label">Total Units</div>
            <div className="font-display text-3xl">{pl.total_units}</div>
            <div className="text-[10px] text-zinc-500">{pl.units_per_box}/box</div>
          </div>
        </div>

        <div className="mt-5">
          <div className="brut-label mb-2">Boxes in this Shipment</div>
          <table className="brut-table">
            <thead><tr><th>#</th><th>Box #</th><th>Product</th><th>Units</th><th>Components</th><th>QR</th></tr></thead>
            <tbody>
              {boxes.map((b, i) => (
                <tr key={b.id}>
                  <td>{i + 1}</td>
                  <td className="font-bold">
                    <Link to={`/boxes/${b.id}`} className="underline">{b.box_number}</Link>
                  </td>
                  <td>{b.product_name}</td>
                  <td className="font-mono-ui">{b.units_count}</td>
                  <td>{b.components?.length || 0}</td>
                  <td>
                    <QRCodeSVG value={`${window.location.origin}/trace?box=${b.box_number}`} size={50} bgColor="#fff" fgColor="#1e293b" level="L" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pl.remarks && (
          <div className="mt-5 brut-border p-3">
            <div className="brut-label">Remarks</div>
            <div className="text-sm">{pl.remarks}</div>
          </div>
        )}

        <div className="mt-10 grid grid-cols-3 gap-6 text-xs">
          {["Packed By", "Verified By", "Shipped By"].map((s) => (
            <div key={s} className="border-t-2 border-[#334155] pt-2 text-center">
              <div className="brut-label">{s}</div>
              <div className="h-12" />
              <div className="text-zinc-500">Signature & Stamp</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
